import React, { useState, useEffect, useRef, memo } from 'react';
import { TrendingUp, TrendingDown, Activity, Hash, Layers, ArrowUpRight, ArrowDownRight, Minus, Globe, Compass, Clock } from 'lucide-react';
import { cn } from '../lib/utils';
import { Card } from './ui/card';
import { motion } from 'framer-motion';

import { useDashboardData } from '../hooks/useDashboardData';
import { useSettingsStore } from '../stores/settingsStore';
import { useIndexIndicators } from '../hooks/useIndexIndicators';
import { useCurrentFuture } from '../hooks/useCurrentFuture';
import { useMarketStore } from '../services/store';
import { GeometricAccordion } from './ui/GeometricAccordion';

// ---------------------------------------------------------
// User Provided Types & Components (Tweaked for dynamic data)
// ---------------------------------------------------------

export interface MarketData {
  name: string;
  currentValue: number;
  diff: number;
  futureLtp: number;
  futureClose: number;
  futureDayChange: number;
  futureDayChangePct: number;
  futureBasis: number;
  futureBasisPct: number;
  isFuturePremium: boolean;
  futureExpiry?: string;
  futureContractName?: string;
  dayLow: number;
  dayHigh: number;
  dayOpen: number;
  idx5mLow: number;
  idx5mHigh?: number;
}

type PillColor = 'green' | 'red' | 'blue' | 'black' | 'gray';

interface BottomPill {
  text: string;
  color: PillColor;
}

interface TopPill {
  text: string | number;
  color: PillColor;
  large?: boolean;
  glowing?: boolean;
  dottedLine?: boolean;
}

interface MarkerData {
  value: number;
  topPills: TopPill[];
  bottomPills: BottomPill[];
  dotTypes: ('solid' | 'ring' | 'tiny' | 'glow')[];
  dotColors: PillColor[];
}

const formatIndianNumber = (num: number, hideDecimals?: boolean) => {
  if (isNaN(num)) return num.toString();
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: hideDecimals ? 0 : 2,
    maximumFractionDigits: hideDecimals ? 0 : 2,
  }).format(num);
};

function getStyles(color: PillColor) {
  switch (color) {
    case 'green': return { text: 'text-teal-700 dark:text-teal-300', bg: 'bg-teal-50 dark:bg-teal-950/60', border: 'border-teal-200 dark:border-teal-800/60', solid: 'bg-teal-500', glow: 'shadow-[0_0_15px_rgba(20,184,166,0.3)]' };
    case 'red': return { text: 'text-rose-700 dark:text-rose-300', bg: 'bg-rose-50 dark:bg-rose-950/60', border: 'border-rose-200 dark:border-rose-800/60', solid: 'bg-rose-500', glow: 'shadow-[0_0_15px_rgba(244,63,94,0.3)]' };
    case 'blue': return { text: 'text-sky-700 dark:text-sky-300', bg: 'bg-sky-50 dark:bg-sky-950/60', border: 'border-sky-200 dark:border-sky-800/60', solid: 'bg-sky-500', glow: 'shadow-[0_0_15px_rgba(14,165,233,0.3)]' };
    case 'black': return { text: 'text-slate-900 dark:text-slate-100', bg: 'bg-slate-100 dark:bg-slate-800', border: 'border-slate-300 dark:border-slate-600', solid: 'bg-slate-900 dark:bg-slate-100', glow: 'shadow-[0_0_15px_rgba(15,23,42,0.2)]' };
    case 'gray': return { text: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800/70', border: 'border-slate-200 dark:border-slate-700', solid: 'bg-slate-400 dark:bg-slate-500', glow: '' };
  }
}

const getMarkers = (data: MarketData) => {
  const map = new Map<number, MarkerData>();
  
  const add = (
    value: number, 
    bottom?: BottomPill, 
    top?: TopPill, 
    dotType: 'solid'|'ring'|'tiny'|'glow' = 'tiny', 
    dotColor: PillColor = 'gray'
  ) => {
    if (value <= 0) return; // ignore missing values
    if (!map.has(value)) {
      map.set(value, { value, topPills: [], bottomPills: [], dotTypes: [], dotColors: [] });
    }
    const m = map.get(value)!;
    if (bottom && !m.bottomPills.find(p => p.text === bottom.text)) m.bottomPills.push(bottom);
    if (top && !m.topPills.find(p => p.text === top.text)) m.topPills.push(top);
    m.dotTypes.push(dotType);
    m.dotColors.push(dotColor);
  };

  // Add lower priority first
  if (data.idx5mLow > 0) add(data.idx5mLow, { text: '5M LOW', color: 'red' }, { text: formatIndianNumber(data.idx5mLow, true), color: 'red' }, 'tiny', 'gray');
  if (data.idx5mHigh && data.idx5mHigh > 0) add(data.idx5mHigh, { text: '5M HIGH', color: 'blue' }, { text: formatIndianNumber(data.idx5mHigh, true), color: 'blue' }, 'tiny', 'gray');
  if (data.dayLow > 0) add(data.dayLow, { text: 'DAY LOW', color: 'green' }, { text: formatIndianNumber(data.dayLow, true), color: 'green' }, 'tiny', 'gray');
  if (data.dayOpen > 0) add(data.dayOpen, { text: 'DAY OPEN', color: 'blue' }, { text: formatIndianNumber(data.dayOpen, true), color: 'blue' }, 'tiny', 'gray');
  if (data.dayHigh > 0) add(data.dayHigh, { text: 'DAY HIGH', color: 'red' }, { text: formatIndianNumber(data.dayHigh, true), color: 'red' }, 'tiny', 'gray');
  

  // Current Value
  if (data.currentValue > 0) add(data.currentValue, undefined, { text: formatIndianNumber(data.currentValue), color: 'black', large: true, glowing: true, dottedLine: true }, 'solid', 'black');

  return Array.from(map.values()).sort((a, b) => a.value - b.value);
};

const Marker: React.FC<{ marker: MarkerData, getPercent: (v: number) => number }> = ({ marker, getPercent }) => {
  const left = getPercent(marker.value);
  const hasDottedLine = marker.topPills.some(p => p.dottedLine);
  
  return (
    <div className="absolute top-1/2 -translate-y-1/2 z-20 flex flex-col items-center" style={{ left: `${left}%` }}>
      
      {/* Top Pills Area */}
      {marker.topPills.length > 0 && (
        <div className={cn(
          "absolute left-1/2 -translate-x-1/2 flex space-x-1.5 items-end",
          hasDottedLine ? "bottom-[24px]" : "bottom-[24px]"
        )}>
          {marker.topPills.map((pill, i) => (
            <div key={i} className="flex flex-col items-center group">
              <div className={cn(
                 "border font-mono font-medium rounded-[4px] whitespace-nowrap text-center relative z-10 transition-all duration-300 backdrop-blur-md",
                 getStyles(pill.color).text,
                 getStyles(pill.color).border,
                 getStyles(pill.color).bg,
                 pill.large ? "text-xs px-2.5 py-1" : "text-[10px] px-1.5 py-0.5 opacity-80",
                 pill.glowing ? getStyles(pill.color).glow : ""
              )}>
                 {pill.text}
              </div>
              {pill.dottedLine ? (
                <div className="absolute top-full left-1/2 -translate-x-1/2 h-[32px] w-px border-l border-dashed border-slate-300 dark:border-slate-600 mt-1 opacity-100" />
              ) : (
                <div className="absolute top-full left-1/2 -translate-x-1/2 h-[16px] w-px border-l border-solid border-slate-300 dark:border-slate-600 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Dot Area */}
      <div className="flex space-x-0.5 items-center justify-center relative z-20 h-4">
        {marker.dotTypes.map((type, i) => {
           const color = marker.dotColors[i] || 'gray';
           const styles = getStyles(color);
           
           if (type === 'glow') {
             return (
                <div key={i} className="relative flex items-center justify-center">
                   <div className={cn("absolute w-4 h-4 rounded-full opacity-40 animate-pulse", styles.solid, styles.glow)} />
                   <div className={cn("w-2 h-2 rounded-full relative z-10", styles.solid)} />
                </div>
             );
           }
           if (type === 'solid') {
             return <div key={i} className={cn("w-2 h-2 rounded-full ring-2 ring-white dark:ring-slate-900", styles.solid, styles.glow)} />;
           }
           if (type === 'ring') {
             return <div key={i} className={cn("w-2.5 h-2.5 rounded-full border-[2px] bg-white dark:bg-slate-900", styles.border)} />;
           }
           return <div key={i} className="w-1 h-3 rounded-full bg-slate-300 dark:bg-slate-600" />;
        })}
      </div>

      {/* Bottom Pills Area */}
      {marker.bottomPills.length > 0 && (
        <div className="absolute top-[28px] left-1/2 -translate-x-1/2 flex space-x-1 z-10">
          {marker.bottomPills.map((pill, i) => (
            <span key={i} className={cn(
              "px-1.5 py-0.5 font-sans font-semibold rounded-[4px] whitespace-nowrap text-[8.5px] tracking-[0.15em] uppercase backdrop-blur-md border",
              getStyles(pill.color).bg,
              getStyles(pill.color).text,
              getStyles(pill.color).border,
            )}>
              {pill.text}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export const MarketGauge: React.FC<{ data: MarketData }> = ({ data }) => {
  const [trend, setTrend] = useState<'up' | 'down' | 'none'>('none');
  const prevValueRef = useRef(data.currentValue);

  useEffect(() => {
    if (data.currentValue > prevValueRef.current) {
      setTrend('up');
    } else if (data.currentValue < prevValueRef.current) {
      setTrend('down');
    }
    prevValueRef.current = data.currentValue;
  }, [data.currentValue]);

  const markers = getMarkers(data);
  const values = markers.map(m => m.value);
  const dataMin = values.length > 0 ? Math.min(...values) : 0;
  const dataMax = values.length > 0 ? Math.max(...values) : 0;
  
  const rawRange = dataMax - dataMin;
  const range = rawRange === 0 ? 100 : rawRange;
  
  const minVal = dataMin - range * 0.15;
  const maxVal = dataMax + range * 0.15;
  const totalRange = maxVal - minVal;

  const getPercent = (val: number) => {
    if (totalRange === 0) return 50;
    return ((val - minVal) / totalRange) * 100;
  };

  const pMin = getPercent(dataMin);
  const pDayLow = data.dayLow > 0 ? getPercent(data.dayLow) : pMin;
  const pDayHigh = data.dayHigh > 0 ? getPercent(data.dayHigh) : pMin;
  const pMax = getPercent(dataMax);
  
  const isPositive = data.diff >= 0;
  const futDiff = (data.futureLtp > 0 && data.currentValue > 0)
    ? data.futureBasis
    : null;
  const isFutDiffPos = data.isFuturePremium;
  const futDiffPct = data.futureBasisPct;
  const daySpan = (data.dayHigh > 0 && data.dayLow > 0) ? (data.dayHigh - data.dayLow) : 0;
  const pCurrent = data.currentValue > 0 ? getPercent(data.currentValue) : pDayLow;
  const pDayOpen = data.dayOpen > 0 ? getPercent(data.dayOpen) : null;

  // Track segments based on Low, LTP, High (and Open)
  const segments: { left: number; width: number; className: string }[] = [];

  if (data.dayLow > 0 && data.dayHigh > 0 && data.dayHigh >= data.dayLow) {
    if (pDayOpen !== null && pDayOpen >= pDayLow && pDayOpen <= pDayHigh) {
      if (pCurrent >= pDayOpen) {
        // Low -> Open (Green)
        if (pDayOpen > pDayLow) {
          segments.push({
            left: pDayLow,
            width: pDayOpen - pDayLow,
            className: "bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-400 shadow-[0_0_14px_rgba(16,185,129,0.7)]",
          });
        }
        // Open -> LTP (Blue / Cyan)
        const ltpEnd = Math.min(pCurrent, pDayHigh);
        if (ltpEnd > pDayOpen) {
          segments.push({
            left: pDayOpen,
            width: ltpEnd - pDayOpen,
            className: "bg-gradient-to-r from-sky-500 via-cyan-400 to-blue-500 shadow-[0_0_14px_rgba(14,165,233,0.7)]",
          });
        }
        // LTP -> High (Red / Rose)
        if (pDayHigh > pCurrent) {
          segments.push({
            left: pCurrent,
            width: pDayHigh - pCurrent,
            className: "bg-gradient-to-r from-rose-400 via-rose-500 to-red-600 shadow-[0_0_14px_rgba(244,63,94,0.6)]",
          });
        }
      } else {
        // Current is below Open (LTP < Open)
        // Low -> LTP (Green)
        const ltpStart = Math.max(pCurrent, pDayLow);
        if (ltpStart > pDayLow) {
          segments.push({
            left: pDayLow,
            width: ltpStart - pDayLow,
            className: "bg-gradient-to-r from-emerald-500 to-teal-400 shadow-[0_0_14px_rgba(16,185,129,0.7)]",
          });
        }
        // LTP -> Open (Amber / Orange warning)
        if (pDayOpen > ltpStart) {
          segments.push({
            left: ltpStart,
            width: pDayOpen - ltpStart,
            className: "bg-gradient-to-r from-amber-400 to-orange-500 shadow-[0_0_14px_rgba(245,158,11,0.6)]",
          });
        }
        // Open -> High (Red / Rose)
        if (pDayHigh > pDayOpen) {
          segments.push({
            left: pDayOpen,
            width: pDayHigh - pDayOpen,
            className: "bg-gradient-to-r from-rose-400 via-rose-500 to-red-600 shadow-[0_0_14px_rgba(244,63,94,0.6)]",
          });
        }
      }
    } else {
      // Direct Low -> LTP -> High
      const ltpClamped = Math.max(pDayLow, Math.min(pCurrent, pDayHigh));
      // Low -> LTP (Green to Cyan)
      if (ltpClamped > pDayLow) {
        segments.push({
          left: pDayLow,
          width: ltpClamped - pDayLow,
          className: "bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-500 shadow-[0_0_14px_rgba(20,184,166,0.7)]",
        });
      }
      // LTP -> High (Rose to Red)
      if (pDayHigh > ltpClamped) {
        segments.push({
          left: ltpClamped,
          width: pDayHigh - ltpClamped,
          className: "bg-gradient-to-r from-rose-400 via-rose-500 to-red-600 shadow-[0_0_14px_rgba(244,63,94,0.6)]",
        });
      }
    }
  }

  return (
    <Card className="w-full mx-auto bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-slate-200 dark:border-slate-800/80 shadow-md overflow-hidden rounded-[20px] ring-1 ring-slate-900/5 dark:ring-white/10 mb-1.5">
      
      {/* Top Section - Dashboard Overview */}
      <div className="p-4 sm:p-5 md:p-6 pb-12 sm:pb-14 md:pb-16 flex flex-col md:flex-row justify-between items-start md:items-center gap-3.5 md:gap-4 lg:gap-6 bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-900 dark:to-slate-950 relative overflow-hidden">
        
        {/* Glow overlay */}
        <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-teal-100 dark:bg-teal-900/20 rounded-full blur-[90px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />

        {/* Main Price Block */}
        <div className="space-y-1 sm:space-y-1.5 md:space-y-2 relative z-10 w-full md:w-auto shrink-0">
          <div className="flex items-center space-x-2">
             <div className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-teal-600 dark:text-teal-400 shadow-xs backdrop-blur-md">
                <Activity className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
             </div>
             <h2 className="text-[11px] sm:text-xs font-heading font-bold tracking-[0.15em] text-slate-500 dark:text-slate-400 uppercase flex items-center gap-2">
                {data.name} 
                <div className="flex space-x-1 items-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-ping" />
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse delay-75" />
                </div>
             </h2>
          </div>
          
          {/* Index LTP Price Display */}
          <div className="flex items-center gap-2">
            <span className="text-3xl sm:text-3xl md:text-[34px] lg:text-4xl xl:text-[42px] font-black tracking-tight text-slate-900 dark:text-slate-50 font-mono drop-shadow-sm leading-none">
              {formatIndianNumber(data.currentValue)}
            </span>
            <div className="flex flex-col justify-center">
               {trend === 'up' && <ArrowUpRight className="w-5 h-5 sm:w-6 sm:h-6 text-teal-500 animate-pulse stroke-[2.5]" />}
               {trend === 'down' && <ArrowDownRight className="w-5 h-5 sm:w-6 sm:h-6 text-rose-500 animate-pulse stroke-[2.5]" />}
               {trend === 'none' && <Minus className="w-5 h-5 sm:w-6 sm:h-6 text-slate-300 dark:text-slate-600 stroke-[2.5]" />}
            </div>
          </div>

          {/* Day Change & Day Change % - Directly BELOW Index LTP */}
          <div className="flex items-center gap-1.5 sm:gap-2 pt-0.5">
            <div className={cn(
              "flex items-center space-x-1 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg text-xs md:text-[12.5px] font-mono font-bold border shadow-xs transition-all duration-300",
              isPositive 
                ? "bg-teal-50 dark:bg-teal-950/60 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800/60" 
                : "bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/60"
            )}>
              {isPositive ? <TrendingUp className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> : <TrendingDown className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
              <span>{isPositive ? '+' : ''}{formatIndianNumber(data.diff)}</span>
            </div>
            {data.currentValue > 0 && (
              <div className={cn(
                "flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg text-xs md:text-[12.5px] tracking-wide font-black font-mono border shadow-xs backdrop-blur-xs transition-all duration-300",
                isPositive 
                  ? "bg-teal-50/70 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400 border-teal-200 dark:border-teal-800/50" 
                  : "bg-rose-50/70 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800/50"
              )}>
                {isPositive ? '+' : ''}{((data.diff / (data.currentValue - data.diff)) * 100).toFixed(2)}%
              </div>
            )}
          </div>
        </div>

        {/* Secondary Metrics: Current Future, Difference & Day Range */}
        <div className="flex flex-nowrap items-stretch gap-2 sm:gap-2.5 md:gap-3 lg:gap-3.5 xl:gap-4 w-full md:w-auto md:flex-1 md:justify-end relative z-10 min-w-0 overflow-x-auto md:overflow-x-visible pb-1 md:pb-0">
          {/* 1. Current Future Card */}
          <TopMetricCard
            label={`${data.name} FUT`}
            badge="LIVE"
            badgeColor="blue"
            value={data.futureLtp > 0 ? formatIndianNumber(data.futureLtp) : '—'}
            subtext={data.futureExpiry ? `Exp: ${data.futureExpiry}` : `${data.name} Future`}
            icon={<Globe className="w-3.5 h-3.5" />}
            theme="blue"
          />

          {/* 2. Difference Card */}
          <TopMetricCard
            label={<span><span className="inline lg:hidden">DIFF</span><span className="hidden lg:inline">DIFFERENCE</span></span>}
            badge={futDiff !== null ? (isFutDiffPos ? "PREMIUM" : "DISCOUNT") : undefined}
            badgeColor={isFutDiffPos ? "teal" : "rose"}
            value={futDiff !== null ? `${isFutDiffPos ? '+' : ''}${formatIndianNumber(futDiff)}` : '—'}
            subtext={futDiff !== null ? `${isFutDiffPos ? '+' : ''}${futDiffPct.toFixed(2)}% vs Spot` : `vs ${data.name} Spot`}
            icon={isFutDiffPos ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            theme={isFutDiffPos ? "teal" : "rose"}
          />


          {/* 3. Day Range Card */}
          <TopMetricCard
            label={<span><span className="inline lg:hidden">RANGE</span><span className="hidden lg:inline">DAY RANGE</span></span>}
            badge={daySpan > 0 ? `${daySpan.toFixed(0)} PTS` : undefined}
            badgeColor="slate"
            value={`${formatIndianNumber(data.dayLow, true)} – ${formatIndianNumber(data.dayHigh, true)}`}
            subtext={daySpan > 0 ? `Spread: ${daySpan.toFixed(1)} pts` : 'Session Low to High'}
            icon={<Layers className="w-3.5 h-3.5" />}
            theme="slate"
          />
        </div>
      </div>

      {/* Main Gauge Stage */}
      <div className="p-4 sm:p-5 md:p-6 pt-7 sm:pt-8 md:pt-9 pb-4 sm:pb-5 md:pb-6 relative bg-white/90 dark:bg-slate-900/95 backdrop-blur-3xl -mt-8 sm:-mt-10 md:-mt-12 rounded-t-[20px] md:rounded-t-[26px] shadow-[0_-10px_25px_-5px_rgba(0,0,0,0.03)] dark:shadow-[0_-10px_25px_-5px_rgba(0,0,0,0.3)] border-t border-white/80 dark:border-slate-800 z-20">
        
        {/* Background Grids & FX */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05]" 
             style={{ backgroundImage: 'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        
        {/* The Track */}
        <div className="relative mt-9 sm:mt-11 md:mt-12 mb-12 sm:mb-14 md:mb-16 h-2 sm:h-2.5 w-full max-w-[96%] mx-auto">
          {/* Base Line */}
          <div className="absolute inset-0 bg-slate-200/80 dark:bg-slate-800 rounded-full shadow-[inset_0_1px_2px_rgba(0,0,0,0.12)] dark:shadow-[inset_0_1px_3px_rgba(0,0,0,0.5)]" />
          
          {/* Colored Zones based on Low, LTP, High */}
          <div className="absolute inset-0 rounded-full overflow-hidden">
            {segments.map((seg, idx) => (
              <motion.div
                key={idx}
                initial={{ width: 0 }}
                animate={{ left: `${seg.left}%`, width: `${Math.max(0, seg.width)}%` }}
                transition={{ duration: 1.2, ease: "easeOut", delay: idx * 0.08 }}
                className={cn("absolute top-0 h-full", seg.className)}
              />
            ))}
          </div>

          {/* Markers */}
          <div className="absolute inset-0">
             {markers.map(m => <Marker key={m.value} marker={m} getPercent={getPercent} />)}
          </div>
        </div>
        
        {/* Key Market Levels Grid */}
        <div className="mt-2 sm:mt-3 w-full mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-3.5 md:gap-4 relative z-10">
           <LevelCard 
             label="DAY OPEN" 
             badge="BASE" 
             value={data.dayOpen} 
             currentValue={data.currentValue} 
             icon={<Compass className="w-3.5 h-3.5" />} 
             theme="sky" 
             deltaType="open" 
           />
           <LevelCard 
             label={<span><span className="inline lg:hidden">5M RANGE</span><span className="hidden lg:inline">5M LOW & HIGH</span></span>} 
             badge={data.idx5mHigh && data.idx5mHigh > 0 && data.idx5mLow > 0 ? `${Math.abs(data.idx5mHigh - data.idx5mLow).toFixed(0)} PTS` : "ORB"} 
             value={data.idx5mLow} 
             secondaryValue={data.idx5mHigh} 
             currentValue={data.currentValue} 
             icon={<Clock className="w-3.5 h-3.5" />} 
             theme="indigo" 
             deltaType="5mRange" 
           />
           <LevelCard 
             label="DAY LOW" 
             badge="SUPPORT" 
             value={data.dayLow} 
             currentValue={data.currentValue} 
             icon={<TrendingDown className="w-3.5 h-3.5" />} 
             theme="teal" 
             deltaType="dayLow" 
           />
           <LevelCard 
             label="DAY HIGH" 
             badge="RESIST" 
             value={data.dayHigh} 
             currentValue={data.currentValue} 
             icon={<TrendingUp className="w-3.5 h-3.5" />} 
             theme="rose" 
             deltaType="dayHigh" 
           />
        </div>
      </div>
      
    </Card>
  );
};

// Sub-components
interface TopMetricCardProps {
  label: React.ReactNode;
  badge?: string;
  badgeColor?: 'blue' | 'teal' | 'rose' | 'slate';
  value: string;
  subtext?: React.ReactNode;
  icon: React.ReactNode;
  theme: 'blue' | 'teal' | 'rose' | 'slate';
}

const TopMetricCard: React.FC<TopMetricCardProps> = ({
  label,
  badge,
  badgeColor,
  value,
  subtext,
  icon,
  theme,
}) => {
  const themeStyles = {
    blue: {
      card: "bg-white/95 dark:bg-slate-900/90 border-sky-200/70 dark:border-sky-800/50 hover:border-sky-300 dark:hover:border-sky-700/60 shadow-xs hover:shadow-md",
      glow: "bg-sky-400/15 dark:bg-sky-500/10",
      iconBox: "bg-sky-50 dark:bg-sky-950/60 border-sky-200/70 dark:border-sky-800/60 text-sky-600 dark:text-sky-400",
      valText: "text-slate-900 dark:text-slate-100",
    },
    teal: {
      card: "bg-white/95 dark:bg-slate-900/90 border-teal-200/70 dark:border-teal-800/50 hover:border-teal-300 dark:hover:border-teal-700/60 shadow-xs hover:shadow-md",
      glow: "bg-teal-400/15 dark:bg-teal-500/10",
      iconBox: "bg-teal-50 dark:bg-teal-950/60 border-teal-200/70 dark:border-teal-800/60 text-teal-600 dark:text-teal-400",
      valText: "text-teal-700 dark:text-teal-300",
    },
    rose: {
      card: "bg-white/95 dark:bg-slate-900/90 border-rose-200/70 dark:border-rose-800/50 hover:border-rose-300 dark:hover:border-rose-700/60 shadow-xs hover:shadow-md",
      glow: "bg-rose-400/15 dark:bg-rose-500/10",
      iconBox: "bg-rose-50 dark:bg-rose-950/60 border-rose-200/70 dark:border-rose-800/60 text-rose-600 dark:text-rose-400",
      valText: "text-rose-700 dark:text-rose-300",
    },
    slate: {
      card: "bg-white/95 dark:bg-slate-900/90 border-slate-200/80 dark:border-slate-800/80 hover:border-slate-300 dark:hover:border-slate-700 shadow-xs hover:shadow-md",
      glow: "bg-slate-300/20 dark:bg-slate-700/20",
      iconBox: "bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300",
      valText: "text-slate-900 dark:text-slate-100",
    },
  }[theme];

  const badgeStyles = {
    blue: "bg-sky-50 dark:bg-sky-950/80 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800/80",
    teal: "bg-teal-50 dark:bg-teal-950/80 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800/80",
    rose: "bg-rose-50 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/80",
    slate: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700",
  }[badgeColor || theme];

  return (
    <div className={cn(
      "relative overflow-hidden rounded-xl sm:rounded-2xl border p-2 sm:p-2.5 md:p-3 lg:p-3.5 xl:p-4 transition-all duration-300 group backdrop-blur-md flex flex-col justify-between min-w-[125px] sm:min-w-[135px] md:min-w-[145px] lg:min-w-[185px] xl:min-w-[210px] flex-1 shadow-xs hover:shadow-md",
      themeStyles.card
    )}>
      {/* Ambient Corner Glow */}
      <div className={cn(
        "absolute -top-8 -right-8 w-20 h-20 sm:w-24 sm:h-24 rounded-full blur-2xl pointer-events-none opacity-35 group-hover:opacity-75 transition-opacity duration-500",
        themeStyles.glow
      )} />

      {/* Header Row */}
      <div className="flex items-center justify-between gap-1.5 sm:gap-2 md:gap-2.5 relative z-10 mb-1 sm:mb-1.5">
        <div className="flex items-center gap-1 sm:gap-1.5 min-w-0">
          <div className={cn("p-1 sm:p-1.5 rounded-lg border shrink-0", themeStyles.iconBox)}>
            {icon}
          </div>
          <span className="text-[9.5px] sm:text-[10px] md:text-[10.5px] lg:text-[11px] font-heading font-bold tracking-wider uppercase text-slate-600 dark:text-slate-300 truncate">
            {label}
          </span>
        </div>
        {badge && (
          <span className={cn(
            "px-1.5 sm:px-2 py-0.5 rounded-md text-[7.5px] sm:text-[8px] md:text-[8.5px] lg:text-[9px] font-mono font-bold uppercase tracking-wider border shadow-2xs shrink-0 whitespace-nowrap",
            badgeStyles
          )}>
            {badge}
          </span>
        )}
      </div>

      {/* Main Value & Subtext */}
      <div className="space-y-0.5 sm:space-y-1 relative z-10">
        <div className={cn(
          "text-sm sm:text-base md:text-base lg:text-lg xl:text-xl font-mono font-black tracking-tight leading-tight truncate",
          themeStyles.valText
        )}>
          {value}
        </div>
        {subtext && (
          <div className="text-[8.5px] sm:text-[9px] md:text-[9.5px] lg:text-[10px] font-mono text-slate-500 dark:text-slate-400 truncate">
            {subtext}
          </div>
        )}
      </div>
    </div>
  );
};

interface LevelCardProps {
  label: React.ReactNode;
  badge: string;
  value: number;
  secondaryValue?: number;
  currentValue: number;
  icon: React.ReactNode;
  theme: 'sky' | 'indigo' | 'teal' | 'rose';
  deltaType: 'open' | '5mLow' | '5mRange' | 'dayLow' | 'dayHigh';
}

const LevelCard: React.FC<LevelCardProps> = ({
  label,
  badge,
  value,
  secondaryValue,
  currentValue,
  icon,
  theme,
  deltaType,
}) => {
  const themeStyles = {
    sky: {
      card: "bg-white/90 dark:bg-slate-900/80 border-sky-200/70 dark:border-sky-800/40 hover:border-sky-300 dark:hover:border-sky-700/60 shadow-xs hover:shadow-md",
      glow: "bg-sky-400/15 dark:bg-sky-500/10",
      iconBox: "bg-sky-50 dark:bg-sky-950/60 border-sky-200/70 dark:border-sky-800/60 text-sky-600 dark:text-sky-400",
      badge: "bg-sky-50/90 dark:bg-sky-950/80 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800/80",
      valText: "text-slate-900 dark:text-slate-100",
    },
    indigo: {
      card: "bg-white/90 dark:bg-slate-900/80 border-indigo-200/70 dark:border-indigo-800/40 hover:border-indigo-300 dark:hover:border-indigo-700/60 shadow-xs hover:shadow-md",
      glow: "bg-indigo-400/15 dark:bg-indigo-500/10",
      iconBox: "bg-indigo-50 dark:bg-indigo-950/60 border-indigo-200/70 dark:border-indigo-800/60 text-indigo-600 dark:text-indigo-400",
      badge: "bg-indigo-50/90 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/80",
      valText: "text-slate-900 dark:text-slate-100",
    },
    teal: {
      card: "bg-white/90 dark:bg-slate-900/80 border-teal-200/70 dark:border-teal-800/40 hover:border-teal-300 dark:hover:border-teal-700/60 shadow-xs hover:shadow-md",
      glow: "bg-teal-400/15 dark:bg-teal-500/10",
      iconBox: "bg-teal-50 dark:bg-teal-950/60 border-teal-200/70 dark:border-teal-800/60 text-teal-600 dark:text-teal-400",
      badge: "bg-teal-50/90 dark:bg-teal-950/80 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800/80",
      valText: "text-teal-800 dark:text-teal-200",
    },
    rose: {
      card: "bg-white/90 dark:bg-slate-900/80 border-rose-200/70 dark:border-rose-800/40 hover:border-rose-300 dark:hover:border-rose-700/60 shadow-xs hover:shadow-md",
      glow: "bg-rose-400/15 dark:bg-rose-500/10",
      iconBox: "bg-rose-50 dark:bg-rose-950/60 border-rose-200/70 dark:border-rose-800/60 text-rose-600 dark:text-rose-400",
      badge: "bg-rose-50/90 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/80",
      valText: "text-rose-800 dark:text-rose-200",
    },
  }[theme];

  const renderDelta = () => {
    if (value <= 0 || currentValue <= 0) {
      return <span className="text-slate-400 dark:text-slate-600 text-[10.5px] font-mono font-medium">—</span>;
    }

    if (deltaType === 'open') {
      const diff = currentValue - value;
      const pct = (diff / value) * 100;
      const isPos = diff >= 0;
      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={cn(
            "font-bold font-mono text-[10.5px] sm:text-xs tracking-tight",
            isPos ? "text-teal-600 dark:text-teal-400" : "text-rose-600 dark:text-rose-400"
          )}>
            {isPos ? '+' : ''}{diff.toFixed(1)} ({isPos ? '+' : ''}{pct.toFixed(2)}%)
          </span>
          <span className="text-[8.5px] sm:text-[9px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-sans">
            vs Open
          </span>
        </div>
      );
    }

    if (deltaType === '5mRange') {
      const hasHigh = secondaryValue && secondaryValue > 0;
      const spread = hasHigh ? (secondaryValue - value) : 0;
      const isBreakout = hasHigh && currentValue > secondaryValue;
      const isBreakdown = currentValue < value;

      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          {isBreakout ? (
            <span className="font-bold font-mono text-[10.5px] sm:text-xs tracking-tight text-teal-600 dark:text-teal-400">
              +{(currentValue - secondaryValue!).toFixed(1)} pts Breakout
            </span>
          ) : isBreakdown ? (
            <span className="font-bold font-mono text-[10.5px] sm:text-xs tracking-tight text-rose-600 dark:text-rose-400">
              -{(value - currentValue).toFixed(1)} pts Breakdown
            </span>
          ) : (
            <span className="font-bold font-mono text-[10.5px] sm:text-xs tracking-tight text-indigo-600 dark:text-indigo-400">
              +{(currentValue - value).toFixed(1)} pts above Low
            </span>
          )}
          {spread > 0 && (
            <span className="text-[8.5px] sm:text-[9px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-sans font-medium">
              ({spread.toFixed(0)} pt range)
            </span>
          )}
        </div>
      );
    }

    if (deltaType === '5mLow') {
      const diff = currentValue - value;
      const isPos = diff >= 0;
      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={cn(
            "font-bold font-mono text-[10.5px] sm:text-xs tracking-tight",
            isPos ? "text-teal-600 dark:text-teal-400" : "text-rose-600 dark:text-rose-400"
          )}>
            {isPos ? '+' : ''}{diff.toFixed(1)} pts
          </span>
          <span className="text-[8.5px] sm:text-[9px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-sans">
            {isPos ? 'above early' : 'below early'}
          </span>
        </div>
      );
    }

    if (deltaType === 'dayLow') {
      const rebound = currentValue - value;
      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-bold font-mono text-[10.5px] sm:text-xs tracking-tight text-teal-600 dark:text-teal-400">
            +{rebound.toFixed(1)} pts
          </span>
          <span className="text-[8.5px] sm:text-[9px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-sans">
            rebound
          </span>
        </div>
      );
    }

    if (deltaType === 'dayHigh') {
      const headroom = value - currentValue;
      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-bold font-mono text-[10.5px] sm:text-xs tracking-tight text-rose-600 dark:text-rose-400">
            -{headroom.toFixed(1)} pts
          </span>
          <span className="text-[8.5px] sm:text-[9px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-sans">
            to high
          </span>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={cn(
      "relative overflow-hidden rounded-xl sm:rounded-2xl border p-2.5 sm:p-3 md:p-3.5 transition-all duration-300 group backdrop-blur-md shadow-xs hover:shadow-md",
      themeStyles.card
    )}>
      {/* Subtle Corner Glow */}
      <div className={cn(
        "absolute -top-10 -right-10 w-24 h-24 sm:w-28 sm:h-28 rounded-full blur-2xl pointer-events-none opacity-40 group-hover:opacity-80 transition-opacity duration-500",
        themeStyles.glow
      )} />

      {/* Top Header Row */}
      <div className="flex items-center justify-between gap-1 sm:gap-1.5 relative z-10 mb-1.5 sm:mb-2">
        <div className="flex items-center gap-1 sm:gap-1.5 min-w-0">
          <div className={cn("p-1 sm:p-1.5 rounded-lg border shrink-0", themeStyles.iconBox)}>
            {icon}
          </div>
          <span className="text-[9px] sm:text-[9.5px] md:text-[10px] lg:text-[10.5px] font-heading font-bold tracking-wider uppercase text-slate-600 dark:text-slate-300 truncate">
            {label}
          </span>
        </div>
        <span className={cn(
          "px-1.5 py-0.5 rounded-md text-[7.5px] sm:text-[8px] md:text-[8.5px] font-mono font-bold uppercase tracking-wider border shadow-2xs shrink-0 whitespace-nowrap",
          themeStyles.badge
        )}>
          {badge}
        </span>
      </div>

      {/* Main Price & Delta */}
      <div className="space-y-1 relative z-10">
        {secondaryValue && secondaryValue > 0 ? (
          <div className="flex items-stretch justify-between gap-1 sm:gap-1.5 py-0.5">
            {/* 5M Low */}
            <div className="flex-1 min-w-0">
              <div className="text-[8px] sm:text-[8.5px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 font-sans mb-0.5">
                5M LOW
              </div>
              <div className="text-sm sm:text-base md:text-lg font-mono font-black tracking-tight text-slate-900 dark:text-slate-100 truncate leading-tight">
                {value > 0 ? formatIndianNumber(value, true) : '—'}
              </div>
              <div className="mt-1">
                {value > 0 && currentValue > 0 ? (
                  <span className={cn(
                    "font-bold font-mono text-[10px] sm:text-[10.5px] md:text-xs tracking-tight whitespace-nowrap block truncate",
                    currentValue >= value ? "text-teal-600 dark:text-teal-400" : "text-rose-600 dark:text-rose-400"
                  )}>
                    {currentValue >= value ? '+' : '-'}{Math.abs(currentValue - value).toFixed(1)} pts
                  </span>
                ) : (
                  <span className="text-slate-400 dark:text-slate-600 text-[10px] font-mono">—</span>
                )}
              </div>
            </div>

            {/* Subtle Divider */}
            <div className="w-px bg-slate-200 dark:bg-slate-700/80 shrink-0 self-stretch my-0.5 mx-0.5" />

            {/* 5M High */}
            <div className="flex-1 min-w-0 text-right">
              <div className="text-[8px] sm:text-[8.5px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 font-sans mb-0.5">
                5M HIGH
              </div>
              <div className="text-sm sm:text-base md:text-lg font-mono font-black tracking-tight text-slate-900 dark:text-slate-100 truncate leading-tight">
                {secondaryValue > 0 ? formatIndianNumber(secondaryValue, true) : '—'}
              </div>
              <div className="mt-1">
                {secondaryValue > 0 && currentValue > 0 ? (
                  <span className={cn(
                    "font-bold font-mono text-[10px] sm:text-[10.5px] md:text-xs tracking-tight whitespace-nowrap block truncate",
                    currentValue >= secondaryValue ? "text-teal-600 dark:text-teal-400" : "text-rose-600 dark:text-rose-400"
                  )}>
                    {currentValue >= secondaryValue ? '+' : '-'}{Math.abs(currentValue - secondaryValue).toFixed(1)} pts
                  </span>
                ) : (
                  <span className="text-slate-400 dark:text-slate-600 text-[10px] font-mono">—</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={cn(
              "text-base sm:text-lg md:text-xl font-mono font-black tracking-tight leading-tight",
              themeStyles.valText
            )}>
              {value > 0 ? formatIndianNumber(value, true) : '—'}
            </div>
            <div className="pt-0.5">
              {renderDelta()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};


// ---------------------------------------------------------
// Main Export Component
export const GlobalMarketStatus = memo(function GlobalMarketStatus() {
    const { marketData } = useDashboardData()
    const activeSymbol = useSettingsStore(s => s.symbol) || 'NIFTY'
    const historicalDate = useSettingsStore(s => s.historicalDate)

    const [isExpanded, setIsExpanded] = useState(() => {
        if (typeof window !== 'undefined') {
            const symbolKey = `market_status_expanded_${activeSymbol || 'default'}`;
            const stored = localStorage.getItem(symbolKey);
            if (stored !== null) return stored !== 'false';
            return localStorage.getItem('market_status_expanded') !== 'false';
        }
        return true;
    });

    useEffect(() => {
        if (!activeSymbol) return;
        const stored = localStorage.getItem(`market_status_expanded_${activeSymbol}`);
        if (stored !== null) {
            setIsExpanded(stored !== 'false');
        }
    }, [activeSymbol]);

    useEffect(() => {
        if (!activeSymbol) return;
        localStorage.setItem(`market_status_expanded_${activeSymbol}`, isExpanded.toString());
        localStorage.setItem('market_status_expanded', isExpanded.toString());
    }, [isExpanded, activeSymbol]);

    const { indexIndicators } = useIndexIndicators(activeSymbol, historicalDate)
    const storeSpot = useMarketStore(s => s.indexLtp[activeSymbol] || 0)
    const storeMeta = useMarketStore(s => s.marketMeta[activeSymbol])
    const liveSpot = marketData?.spotPrice || storeSpot || 0
    const liveDiff = marketData?.dayChange || storeMeta?.dayChange || 0

    const timestamp = marketData?.timestamp || storeMeta?.timestamp || Date.now()
    const futureData = useCurrentFuture(activeSymbol, liveSpot, timestamp)

    const data: MarketData = {
        name: activeSymbol,
        currentValue: liveSpot,
        diff: liveDiff,
        futureLtp: futureData.futureLtp || 0,
        futureClose: futureData.closePrice || 0,
        futureDayChange: futureData.dayChange || 0,
        futureDayChangePct: futureData.dayChangePct || 0,
        futureBasis: futureData.basis || 0,
        futureBasisPct: futureData.basisPct || 0,
        isFuturePremium: futureData.isPremium,
        futureExpiry: futureData.expiry,
        futureContractName: futureData.contractName,
        dayLow: (indexIndicators?.todayLow && indexIndicators.todayLow > 0) ? indexIndicators.todayLow : (liveSpot > 0 ? liveSpot : 0),
        dayHigh: (indexIndicators?.todayHigh && indexIndicators.todayHigh > 0) ? indexIndicators.todayHigh : (liveSpot > 0 ? liveSpot : 0),
        dayOpen: (indexIndicators?.todayOpen && indexIndicators.todayOpen > 0) ? indexIndicators.todayOpen : (liveSpot > 0 ? liveSpot : 0),
        idx5mLow: (indexIndicators?.first5Min?.low && indexIndicators.first5Min.low > 0) ? indexIndicators.first5Min.low : (liveSpot > 0 ? liveSpot : 0),
        idx5mHigh: (indexIndicators?.first5Min?.high && indexIndicators.first5Min.high > 0) ? indexIndicators.first5Min.high : 0,
    }

    return (
        <div className="w-full px-2 max-w-[1920px] mx-auto mb-1">
            <GeometricAccordion
                title={`Market Status (${activeSymbol})`}
                badge={<Globe size={18} />}
                isOpen={isExpanded}
                onToggle={() => setIsExpanded(!isExpanded)}
            >
                <div className="w-full relative bg-slate-50 dark:bg-[#060a12] text-slate-900 dark:text-slate-100 font-sans p-1.5 md:p-2 rounded-b-[18px] overflow-hidden">
                    {/* Global Ambient Glows */}
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-sky-100 dark:bg-sky-950/20 rounded-full blur-[120px] pointer-events-none opacity-50" />
                    <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-teal-100 dark:bg-teal-950/20 rounded-full blur-[120px] pointer-events-none opacity-50" />
                    <div className="absolute inset-0 pointer-events-none opacity-[0.02] dark:opacity-[0.04]" 
                         style={{ backgroundImage: 'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

                    <div className="relative z-10 w-full mx-auto">
                        <MarketGauge data={data} />
                    </div>
                </div>
            </GeometricAccordion>
        </div>
    )
})
